<template>
  <Transition name="bg-fade">
    <div v-if="showPopUp" class="background-popup" @click="closePopUp" />
  </Transition>

  <Transition name="popup-fade">
    <div v-if="showPopUp" class="newsletter-pop-up">
      <button @click="closePopUp" class="newsletter-pop-up__close">
        <Icon name="close" />
      </button>
      <div class="newsletter-pop-up__container">
        <div class="newsletter-pop-up__container-text">
          <h2 class="newsletter-pop-up__container-text__title">
            {{ $t("popup.title") }}
          </h2>
          <p class="newsletter-pop-up__container-text__description">
            {{ $t("popup.description") }}
          </p>
        </div>

        <button @click="scrollToNewsletterSection" class="btn btn--gold">
          {{ $t("popup.button") }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import type { FooterBlocksFragment } from "~/lib/api-types";

  const props = defineProps<{
    block: FooterBlocksFragment;
  }>();

  const ONE_WEEK_IN_SECONDS = 60 * 60 * 24 * 7;

  const isDismissed = useCookie<boolean | null>("newsletter-popup-dismissed", {
    maxAge: ONE_WEEK_IN_SECONDS,
    path: "/",
    sameSite: "lax",
  });

  const showPopUp = ref(false);

  const closePopUp = () => {
    showPopUp.value = false;
    document.body.style.overflow = "auto";
    isDismissed.value = true;
  };

  const scrollToNewsletterSection = () => {
    const newsletterSection = document.getElementById("newsletter-form");
    closePopUp();
    if (newsletterSection) {
      setTimeout(() => {
        newsletterSection.scrollIntoView({ behavior: "smooth" });
      }, 200);
    }
  };

  onMounted(() => {
    if (isDismissed.value) {
      return;
    }

    setTimeout(() => {
      if (!isDismissed.value) {
        showPopUp.value = true;
        document.body.style.overflow = "hidden";
      }
    }, 500);
  });

  onBeforeUnmount(() => {
    if (showPopUp.value) {
      document.body.style.overflow = "auto";
    }
  });
</script>

<style lang="scss" scoped>
  .background-popup {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9998;
  }

  .bg-fade-enter-active {
    transition: opacity 0.3s ease;
  }
  .bg-fade-leave-active {
    transition: opacity 0.3s ease 0.3s;
  }
  .bg-fade-enter-from,
  .bg-fade-leave-to {
    opacity: 0;
  }

  .newsletter-pop-up {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9999;

    display: flex;
    width: calc(100% - 12px);
    max-width: 400px;
    height: max-content;
    padding: 34px 24px 24px 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;

    border-radius: 16px;
    background: var(--color-white, #ffffff);
    box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.25);
    color: var(--color-black, #000000);

    @media (min-width: 768px) {
      width: 600px;
      max-width: 100%;
      gap: 24px;
    }

    @media (min-width: 1280px) {
      padding: 53px 48px 48px 48px;
    }

    &__close {
      position: absolute;
      top: 10px;
      right: 10px;
      cursor: pointer;
      color: var(--color-black, #000000);
      width: 24px;
      height: 24px;

      @media (min-width: 1024px) {
        top: _rem(20px);
        right: _rem(20px);
      }
    }

    &__container {
      width: 100%;
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 12px;

      @media (min-width: 768px) {
        gap: 24px;
      }

      &-text {
        display: flex;
        flex-direction: column;
        gap: 8px;

        &__title {
          font-size: 20px;
          font-style: normal;
          font-weight: 300;
          line-height: 1.2;
          margin: 0;

          @media (min-width: 768px) {
            font-size: 24px;
          }

          @media (min-width: 1280px) {
            font-size: 32px;
          }
        }

        &__description {
          font-size: 14px;
          font-weight: 400;
          line-height: 1.5;
          margin: 0;

          @media (min-width: 768px) {
            font-size: 16px;
          }
        }
      }

      .btn {
        width: max-content;

        @media (min-width: 1280px) {
          font-size: 16px;
        }
      }
    }
  }

  .popup-fade-enter-active {
    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    transition-delay: 0.2s;
  }

  .popup-fade-leave-active {
    transition: all 0.3s ease-in;
  }

  .popup-fade-enter-from,
  .popup-fade-leave-to {
    opacity: 0;
    transform: translate(-50%, 50vh) scale(0.95);
  }
</style>
